import { PlannedPowerMeterModel, PlannedPowerMeterType, PowerMeterFormDeviceType } from 'src/Libs/installations/models/subdevice.model'

/** UI-only meter type value gating connection direction and current transformer settings */
export const METER_TYPE_INTERNAL_ROOT_POWER_METER = PowerMeterFormDeviceType.InternalRootPowerMeter

export const POWER_METER_TYPE_BY_FORM_VALUE: Record<PowerMeterFormDeviceType, PlannedPowerMeterType> = {
  [PowerMeterFormDeviceType.InternalRootPowerMeter]: 'InternalRoot',
  [PowerMeterFormDeviceType.ExternalRootPowerMeter]: 'ExternalRoot',
  [PowerMeterFormDeviceType.ProductionMeter]: 'Production',
  [PowerMeterFormDeviceType.ConsumptionMeter]: 'Consumption',
  [PowerMeterFormDeviceType.BidirectionalMeter]: 'Bidirectional',
  [PowerMeterFormDeviceType.ThirdPartyMeter]: 'ThirdParty',
  [PowerMeterFormDeviceType.ControllerBypass]: 'ControllerBypass',
  [PowerMeterFormDeviceType.ProductionMeterWithoutStorage]: 'ProductionWithoutStorage',
}

export const POWER_METER_FORM_VALUE_BY_TYPE: Record<PlannedPowerMeterType, PowerMeterFormDeviceType> = Object.entries(
  POWER_METER_TYPE_BY_FORM_VALUE
).reduce(
  (values, [formValue, type]) => ({ ...values, [type]: formValue as PowerMeterFormDeviceType }),
  {} as Record<PlannedPowerMeterType, PowerMeterFormDeviceType>
)

export const isPlannedPowerMeterType = (deviceType: string): deviceType is PlannedPowerMeterType =>
  Object.values(POWER_METER_TYPE_BY_FORM_VALUE).includes(deviceType as PlannedPowerMeterType)

export const getPlannedPowerMeterType = (
  deviceType: PowerMeterFormDeviceType | PlannedPowerMeterType | undefined
): PlannedPowerMeterType | undefined => {
  if (!deviceType) return undefined
  if (isPlannedPowerMeterType(deviceType)) return deviceType
  return POWER_METER_TYPE_BY_FORM_VALUE[deviceType]
}

/** Device type used by the API to represent the (single) internal root power meter */
const ROOT_POWER_METER_DEVICE_TYPE = 'House'

export const isRootPowerMeter = (meter: Pick<PlannedPowerMeterModel, 'deviceType'> | null | undefined): boolean =>
  meter?.deviceType === ROOT_POWER_METER_DEVICE_TYPE

type PowerMeterWithPlannedDeviceType = Nullable<Pick<PlannedPowerMeterModel, 'id' | 'deviceType'>> | null | undefined

export const isInternalOrExternalRootPowerMeter = (meter: PowerMeterWithPlannedDeviceType): boolean => {
  return meter?.deviceType === 'InternalRoot' || meter?.deviceType === 'ExternalRoot'
}

export const hasExistingInternalOrExternalRootPowerMeter = (
  meters: PowerMeterWithPlannedDeviceType[],
  currentPowerMeter?: PowerMeterWithPlannedDeviceType
): boolean => meters.some(meter => meter && meter.id !== currentPowerMeter?.id && isInternalOrExternalRootPowerMeter(meter))

export const getDefaultPowerMeterFormDeviceType = (
  meters: PowerMeterWithPlannedDeviceType[],
  currentPowerMeter?: PowerMeterWithPlannedDeviceType
): PowerMeterFormDeviceType =>
  hasExistingInternalOrExternalRootPowerMeter(meters, currentPowerMeter)
    ? PowerMeterFormDeviceType.ProductionMeter
    : PowerMeterFormDeviceType.InternalRootPowerMeter

/**
 * Only one internal root power meter is allowed per installation. Determines whether one already
 * exists among the other power meters (excluding the one currently being edited, if any).
 */
export const hasExistingRootPowerMeter = (meters: PlannedPowerMeterModel[], currentPowerMeter?: PlannedPowerMeterModel): boolean =>
  meters.some(meter => meter && meter.id !== currentPowerMeter?.id && isRootPowerMeter(meter))

export const CURRENT_TRANSFORMER_RATIO_OPTIONS: { value: string; label: string }[] = [
  { value: '100:5', label: '100/5 A' },
  { value: '150:5', label: '150/5 A' },
  { value: '200:5', label: '200/5 A' },
  { value: '250:5', label: '250/5 A' },
  { value: '300:5', label: '300/5 A' },
  { value: '400:5', label: '400/5 A' },
  { value: '500:5', label: '500/5 A' },
  { value: '600:5', label: '600/5 A' },
  { value: '800:5', label: '800/5 A' },
  { value: '1000:5', label: '1000/5 A' },
]
